import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Input, message, Row, Spin } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchResetPasswordByToken, fetchResetPasswordEmail } from '../../services';
import {
  formatResetPasswordTime,
  getPasswordStyle,
  RESET_PASSWORD_TOKEN_LIFETIME,
} from './modalResetPassword.utils';
import './modalResetPassword.scss';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isPasswordValid = (password: string) => {
  const hasLetters = /[a-zA-Z]/.test(password);
  const hasNumbers = /\d/.test(password);

  return password.length >= 10 && hasLetters && hasNumbers;
};

const ModalResetPassword: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.user);
  const [email, setEmail] = useState('');
  const [tokenSent, setTokenSent] = useState(false);
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timer, setTimer] = useState(RESET_PASSWORD_TOKEN_LIFETIME);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const passwordStyle = useMemo(() => getPasswordStyle(newPassword), [newPassword]);

  const clearResetState = useCallback(() => {
    setEmail('');
    setToken('');
    setNewPassword('');
    setConfirmPassword('');
    setTokenSent(false);
    setTimer(RESET_PASSWORD_TOKEN_LIFETIME);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    setTimer(RESET_PASSWORD_TOKEN_LIFETIME);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleResetPassword = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      message.error('Please enter your email address');
      return;
    }

    if (!emailRegex.test(normalizedEmail)) {
      message.error('Please enter a valid email address');
      return;
    }

    try {
      await dispatch(fetchResetPasswordEmail({ email: normalizedEmail })).unwrap();
      message.success('A password reset email has been sent.');
      setEmail(normalizedEmail);
      setTokenSent(true);
      startTimer();
    } catch (error: any) {
      message.error(error.message || 'Error sending password reset email');
    }
  }, [dispatch, email, startTimer]);

  const handlePasswordResetByToken = useCallback(async () => {
    if (!token) {
      message.error('Please enter the token you received');
      return;
    }

    if (!newPassword) {
      message.error('Please enter a new password');
      return;
    }

    if (!confirmPassword) {
      message.error('Please confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      message.error('Password does not meet minimum requirements');
      return;
    }

    try {
      await dispatch(fetchResetPasswordByToken({ token, newPassword, email })).unwrap();
      message.success('Password has been successfully reset.');
      clearResetState();
      onCancel();
    } catch (error: any) {
      message.error(error.message || 'Invalid or expired token');
    }
  }, [clearResetState, confirmPassword, dispatch, email, newPassword, onCancel, token]);

  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  return (
    <div className="modal-reset-password">
      <Row gutter={8} className="block-email">
        <Col span={18} className="input-container">
          <label className="input-label" htmlFor="reset-email">Enter email address</label>
          <Input
            id="reset-email"
            placeholder="Enter your email address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="modal-reset-password__input"
          />
        </Col>
        <Col span={6} className="modal-reset-password__action-col">
          <Button
            type="primary"
            onClick={() => void handleResetPassword()}
            disabled={loading}
            className="bouttom-Send"
            aria-label={loading ? 'Sending reset email' : 'Send Email'}
            aria-busy={loading}
          >
            {loading ? <Spin size="small" aria-hidden="true" /> : 'Send Email'}
          </Button>
        </Col>
      </Row>
      {tokenSent && (
        <>
          <label className="input-label" htmlFor="reset-token">Enter the token you received</label>
          <Input
            id="reset-token"
            placeholder="000000"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            className="modal-reset-password__input"
          />

          {timer === 0 && (
            <div className="modal-reset-password__expired">
              Please refresh your token and continue to reset your password.
            </div>
          )}

          <label className="input-label" htmlFor="reset-new-password">Enter new password</label>
          <Input.Password
            id="reset-new-password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            style={passwordStyle}
            className="modal-reset-password__input"
          />

          <label className="input-label" htmlFor="reset-confirm-password">Confirm new password</label>
          <Input.Password
            id="reset-confirm-password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className="modal-reset-password__input"
          />

          <Row justify="space-between" align="middle">
            <Button
              type="primary"
              onClick={() => void handlePasswordResetByToken()}
              disabled={loading || timer === 0}
              aria-label={loading ? 'Resetting password' : 'Reset Password'}
              aria-busy={loading}
            >
              {loading ? <Spin size="small" aria-hidden="true" /> : 'Reset Password'}
            </Button>
            {timer > 0 && <span className="modal-reset-password__timer">{formatResetPasswordTime(timer)}</span>}
          </Row>
        </>
      )}
    </div>
  );
};

export default ModalResetPassword;
